import React, { FunctionComponent, useEffect, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { DateTime } from 'luxon'
import { decode } from 'he'
import { get, isEmpty, pick } from 'lodash'
import { rebuild } from 'react-tooltip'
import accounting from 'accounting'

import { DateTimeDisplay, PageDescription } from '../../../../components'
import Table from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'
import Summary from '../Summary'
import { DetailsTableContext } from './index'

import { State } from '../../../../reducers'
import { getPaymentDetails, exportPayments } from '../../../../actions/payments'
import { pruneObject } from '../../../../utils/objectManagement'

import {
  PaymentsData,
  PaymentSummary,
  Payment,
} from '../../../../typings/payments'
import { ReportStatus } from '../../../../typings/report'

import styles from '../styles.module.css'

interface PaymentDetailsProps {
  paymentId: string
}

const Details: FunctionComponent<PaymentDetailsProps> = ({ paymentId }) => {
  const dispatch = useDispatch()
  const location = useLocation()

  const {
    sort,
    pagination: { page, size },
    changePage,
    changePageSize,
    changeSort,
  } = useContext(DetailsTableContext)

  const previousPageUrl = get(location, 'state.previousUrl', '../history')

  const paymentDetailsLoading = useSelector<State, boolean>(
    state => get(state, 'payments.paymentDetails.status') === 'fetching'
  )

  const paymentDetails = useSelector<State, PaymentsData>(
    state => state.payments.paymentDetails
  )

  const paymentDetailsSummary = useSelector<State, PaymentSummary>(state =>
    get(state, 'payments.paymentDetails.summary', {})
  )

  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.payments.paymentDetails.export, [
      'hasData',
      'status',
      'error',
    ]),
    loading: state.payments.paymentDetails.export.status === 'fetching',
    hasError: !isEmpty(state.payments.paymentDetails.export.error),
  }))

  useEffect(() => {
    rebuild()
  }, [])

  useEffect(() => {
    dispatch(
      getPaymentDetails(paymentId, {
        page,
        size,
        ...pruneObject(sort),
      })
    )
  }, [sort, page, size])

  return (
    <>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Link className={styles.backLink} to={previousPageUrl}>
            Back to Payment History
          </Link>

          <h2>Payment Details</h2>

          <div className={styles.paymentInfo}>
            <strong>Payment ID: </strong>
            {paymentId}
          </div>
        </div>

        <ReportActions
          className={styles.actions}
          fetchExport={() =>
            dispatch(
              exportPayments('paymentDetails', paymentId, pruneObject(sort))
            )
          }
          exportStatus={exportStatus}
        />
      </div>

      <PageDescription
        content={`This report reflects each individual commission included in the payment: ${paymentId}`}
      />

      <Summary data={paymentDetailsSummary} isLoading={paymentDetailsLoading} />
      {/* TODO: Move payment ID into summary?? */}

      <Table
        className={styles.table}
        isLoading={paymentDetailsLoading}
        onSortChange={changeSort}
        sort={sort}
        data={paymentDetails.transactions as Payment[]}
        pagination={{
          pageSize: size,
          currentPage: page,
          total: get(paymentDetails, 'pagination.total', 1),
          onPaginate: changePage,
          onPageSizeUpdate: changePageSize,
        }}
        cols={[
          {
            text: 'Transaction ID',
            field: 'transactionId',
          },
          {
            text: 'Process Date',
            field: 'processDate',
            sortable: true,
            render: ({ processDate }) =>
              processDate && (
                <DateTimeDisplay datetime={DateTime.fromSQL(processDate)} />
              ),
          },
          {
            text: 'Product',
            field: 'productName',
            sortable: true,
            render: ({ productName }) => productName && decode(productName),
          },
          {
            text: 'Advertiser',
            field: 'advertiserName',
            sortable: true,
          },
          {
            text: 'Commission',
            field: 'commission',
            sortable: true,
            className: styles.right,
            render: ({ commission }) => accounting.formatMoney(commission, '$'),
          },
          {
            text: 'Adjustments',
            field: 'adjustments',
            sortable: true,
            className: styles.right,
            render: ({ adjustments }) =>
              accounting.formatMoney(adjustments, '$'),
          },
          {
            text: 'Total Amount',
            field: 'total',
            className: styles.right,
            render: ({ total }) => accounting.formatMoney(total, '$'),
          },
        ]}
        totalRow={[
          {
            text: 'Total',
            colSpan: 6,
            className: styles.footerHeader,
          },
          {
            text: accounting.formatMoney(
              get(paymentDetails, 'totals.total', 0),
              '$'
            ),
            className: styles.right,
          },
        ]}
      />
    </>
  )
}

export default Details
